/**
 * WhatsAppInboxAdmin.jsx — In-app WhatsApp inbox for admin.
 *
 * Layout: left = contact list, right = thread viewer + admin reply box +
 * "Create case + payment link" panel that reuses the existing
 * POST /api/admin/consultations/create-manual endpoint.
 *
 * Backend:
 *   GET  /admin/whatsapp/chats
 *   GET  /admin/whatsapp/chats/{phone}
 *   POST /admin/whatsapp/chats/{phone}/send
 *   POST /admin/consultations/create-manual   (case + Cashfree link)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Send, Search, Copy, ExternalLink, RefreshCw, User, Phone, PlusCircle } from "lucide-react";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  const d = Math.floor(s / 86400);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

function CreateCasePanel({ phone, patient, onCreated }) {
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({
    name: patient?.name || "",
    age: patient?.age || "",
    priority: "standard",
    fee_inr: 1000,
    doctor_id: "",
    concern: "",
    symptoms: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/doctors").then(r => {
      setDoctors(r.data || []);
      const sathya = (r.data || []).find(d => (d.name || "").toLowerCase().includes("sathya"));
      if (sathya) setForm(prev => ({ ...prev, doctor_id: sathya.id }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Fee reflects the new pricing tiers (Regular ₹1,000 · Emergency ₹2,000 · Video ₹2,000).
    // Backend pricing_service is authoritative — this is display-only.
    setForm(prev => ({
      ...prev,
      fee_inr: prev.priority === "standard" ? 1000 : 2000,
    }));
  }, [form.priority]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Patient name required"); return; }
    if (!form.doctor_id) { toast.error("Select a doctor"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/consultations/create-manual", {
        ...form,
        phone,
        notes: `Created from WhatsApp inbox for ${phone}`,
      });
      const url = r.data.payment_url;
      if (!url) {
        toast.error("Case created but Cashfree link failed — check /admin");
        onCreated && onCreated(r.data);
        return;
      }
      toast.success(`Case ${r.data.case_short_id} created — link ready`);
      onCreated && onCreated({ ...r.data, payment_url: url });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create case");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 border border-slate-200 rounded-lg bg-slate-50 space-y-2" data-testid="wa-inbox-create-case-panel">
      <div className="font-semibold text-xs text-slate-800 flex items-center gap-1">
        <PlusCircle className="w-3.5 h-3.5"/> Create Case + Cashfree Link
      </div>
      <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Patient name" className="h-8 text-xs" data-testid="wa-inbox-cc-name"/>
      <div className="grid grid-cols-2 gap-2">
        <Input value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} placeholder="Age" type="number" className="h-8 text-xs" data-testid="wa-inbox-cc-age"/>
        <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="h-8 text-xs rounded-md border border-slate-300 bg-white px-2" data-testid="wa-inbox-cc-priority">
          <option value="standard">Regular ₹1,000</option>
          <option value="urgent">Priority ₹2,000</option>
          <option value="video">Video Consultation ₹2,000</option>
        </select>
      </div>
      <select value={form.doctor_id} onChange={e => setForm({ ...form, doctor_id: e.target.value })} className="h-8 text-xs rounded-md border border-slate-300 bg-white px-2 w-full" data-testid="wa-inbox-cc-doctor">
        <option value="">Select doctor...</option>
        {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
      <Input value={form.concern} onChange={e => setForm({ ...form, concern: e.target.value })} placeholder="Chief complaint (e.g. Brain MRI review)" className="h-8 text-xs" data-testid="wa-inbox-cc-concern"/>
      <textarea value={form.symptoms} onChange={e => setForm({ ...form, symptoms: e.target.value })} placeholder="Symptoms / notes" rows={2} className="text-xs w-full rounded-md border border-slate-300 bg-white px-2 py-1 resize-y" data-testid="wa-inbox-cc-symptoms"/>
      <Input value={form.fee_inr} onChange={e => setForm({ ...form, fee_inr: Number(e.target.value) })} placeholder="Fee (INR)" type="number" className="h-8 text-xs" data-testid="wa-inbox-cc-fee"/>
      <Button onClick={submit} disabled={busy} className="w-full h-8 text-xs" data-testid="wa-inbox-cc-submit">
        {busy ? "Creating…" : "Create case + Get link"}
      </Button>
    </div>
  );
}

function Thread({ phone, onSent }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/whatsapp/chats/${encodeURIComponent(phone)}`);
      setThread(r.data);
    } catch (e) {
      toast.error("Failed to load thread");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); setLastLink(null); }, [phone]);

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/whatsapp/chats/${encodeURIComponent(phone)}/send`, { text: msg });
      if (r.data.ok) {
        toast.success("WhatsApp sent");
        setMsg("");
        load();
        onSent && onSent(phone);
      } else {
        toast.error("Interakt rejected the message (outside 24h window? Use a template.)");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (!lastLink) return;
    navigator.clipboard.writeText(lastLink);
    toast.success("Link copied");
  };

  const sendLinkAsReply = () => {
    if (!lastLink) return;
    const text = `Hi ${thread?.patient?.name || "there"} — thank you for reaching out to NeuroOpinion India. Your case is registered and Dr. ${thread?.cases?.[0]?.doctor || "Sathya Reddy Kalahasti (NIMHANS-trained Neurosurgeon)"} will review after payment.\n\n💳 Complete payment here (₹1,000): ${lastLink}\n\nOnce paid, you'll get automatic updates: Payment confirmed → Doctor assigned → Report in 24-48h.`;
    setMsg(text);
  };

  if (loading) return <div className="p-6 text-sm text-slate-400">Loading thread…</div>;
  if (!thread) return null;

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-slate-200 p-3 flex items-center justify-between bg-white">
        <div>
          <div className="font-semibold text-slate-900 flex items-center gap-1.5">
            <Phone className="w-4 h-4 text-emerald-600"/>
            {thread.patient?.name || thread.phone}
          </div>
          <div className="text-[11px] text-slate-500 font-mono">{thread.phone} · {thread.message_count} msgs</div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`https://wa.me/${thread.phone.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-700 underline">
            Open in WhatsApp <ExternalLink className="w-3 h-3 inline"/>
          </a>
          <Button variant="ghost" size="sm" onClick={load} data-testid="wa-inbox-thread-refresh">
            <RefreshCw className="w-3.5 h-3.5"/>
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0MCA0MCI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjZjBlN2RhIi8+PC9zdmc+')]" data-testid="wa-inbox-messages">
          {thread.messages.map((m, i) => (
            <div key={i} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[70%] rounded-lg px-3 py-2 shadow-sm text-[13px] ${m.direction === "out" ? "bg-emerald-100 text-slate-900" : "bg-white text-slate-900"}`} data-testid={`wa-inbox-msg-${m.direction}-${i}`}>
                <div className="whitespace-pre-wrap break-words">{m.text || `[${m.type || "media"}]`}</div>
                <div className="text-[10px] text-slate-500 mt-1 flex items-center justify-end gap-1">
                  {m.template && <span className="text-[9px] px-1 bg-blue-100 text-blue-700 rounded">tpl:{m.template}</span>}
                  {timeAgo(m.at)}
                  {m.direction === "out" && !m.ok && <span className="text-red-600">·failed</span>}
                </div>
              </div>
            </div>
          ))}
          {thread.messages.length === 0 && <div className="text-xs text-slate-400 italic text-center py-4">No messages yet.</div>}
        </div>

        {/* Side panel: patient info + create case */}
        <div className="w-80 border-l border-slate-200 overflow-y-auto p-3 space-y-3 bg-slate-50">
          {thread.patient && (
            <div className="p-3 border border-slate-200 rounded-lg bg-white text-xs space-y-1" data-testid="wa-inbox-patient-info">
              <div className="font-semibold text-slate-900 flex items-center gap-1"><User className="w-3 h-3"/>Patient</div>
              <div><b>Name:</b> {thread.patient.name}</div>
              <div><b>Role:</b> {thread.patient.role}</div>
              {thread.patient.age && <div><b>Age:</b> {thread.patient.age}</div>}
              {thread.patient.email && <div className="truncate"><b>Email:</b> {thread.patient.email}</div>}
              {thread.patient.registration_source && <div><b>Source:</b> {thread.patient.registration_source}</div>}
            </div>
          )}

          {thread.cases && thread.cases.length > 0 && (
            <div className="p-3 border border-slate-200 rounded-lg bg-white text-xs space-y-1">
              <div className="font-semibold text-slate-900">Existing cases</div>
              {thread.cases.map(c => (
                <div key={c.id} className="border-l-2 border-slate-200 pl-2 py-0.5" data-testid={`wa-inbox-case-${c.id}`}>
                  <div className="font-mono text-[10px]">{c.case_id || c.id.slice(0, 8)}</div>
                  <div>{c.doctor_name} · ₹{c.fee_inr} · <span className={c.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}>{c.payment_status}</span></div>
                </div>
              ))}
            </div>
          )}

          <CreateCasePanel
            phone={phone}
            patient={thread.patient}
            onCreated={(res) => {
              setLastLink(res.payment_url);
              load();
            }}
          />

          {lastLink && (
            <div className="p-3 border border-emerald-300 bg-emerald-50 rounded-lg space-y-2" data-testid="wa-inbox-payment-link-panel">
              <div className="text-xs font-semibold text-emerald-900">✅ Payment link ready</div>
              <div className="text-[10px] font-mono break-all bg-white p-2 rounded border border-emerald-200">{lastLink}</div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={copyLink} className="flex-1 h-7 text-[11px]" data-testid="wa-inbox-copy-link">
                  <Copy className="w-3 h-3 mr-1"/>Copy
                </Button>
                <Button size="sm" onClick={sendLinkAsReply} className="flex-1 h-7 text-[11px]" data-testid="wa-inbox-use-link-in-reply">
                  Compose reply
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-slate-200 p-3 bg-white">
        <div className="flex gap-2">
          <textarea
            value={msg}
            onChange={e => setMsg(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Reply to patient (session-mode, works only if last inbound < 24h)…"
            rows={2}
            className="flex-1 text-sm rounded-md border border-slate-300 px-3 py-2 resize-none"
            data-testid="wa-inbox-composer"
          />
          <Button onClick={send} disabled={sending || !msg.trim()} className="h-auto" data-testid="wa-inbox-send-btn">
            <Send className="w-4 h-4"/>
          </Button>
        </div>
        <div className="text-[10px] text-slate-500 mt-1">⚠ Meta rule: plain-text replies only work within 24h of last inbound. For older threads use a template via /admin/whatsapp-diag.</div>
      </div>
    </div>
  );
}

export default function WhatsAppInboxAdmin() {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/whatsapp/chats");
      setChats(r.data.chats || []);
    } catch (e) {
      toast.error("Failed to load inbox");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return chats;
    const q = search.trim().toLowerCase();
    return chats.filter(c => (c.phone || "").includes(q) || (c.patient_name || "").toLowerCase().includes(q) || (c.last_message || "").toLowerCase().includes(q));
  }, [chats, search]);

  return (
    <Card className="mt-6 p-0 border-slate-200 overflow-hidden" data-testid="admin-whatsapp-inbox">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2">
          <span className="text-lg">💬</span>
          <h3 className="font-heading text-lg font-semibold text-slate-900">WhatsApp Inbox</h3>
          <span className="text-xs text-slate-500">({filtered.length} of {chats.length} chats)</span>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="wa-inbox-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1"/>Refresh
        </Button>
      </div>

      <div className="flex" style={{ height: "600px" }}>
        {/* Contact list */}
        <div className="w-72 border-r border-slate-200 flex flex-col">
          <div className="p-2 border-b border-slate-200">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
              <Input className="pl-7 h-8 text-xs" placeholder="Search phone / name / text" value={search} onChange={e => setSearch(e.target.value)} data-testid="wa-inbox-search"/>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-xs text-slate-400">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-xs text-slate-400 italic">No conversations yet.</div>
            ) : filtered.map(c => (
              <button
                key={c.digits}
                onClick={() => setSelected(c.phone)}
                className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${selected === c.phone ? "bg-emerald-50" : ""}`}
                data-testid={`wa-inbox-contact-${c.digits}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900 text-sm truncate">{c.patient_name || c.phone}</div>
                  <div className="text-[10px] text-slate-400 shrink-0">{timeAgo(c.last_at)}</div>
                </div>
                <div className="text-[11px] text-slate-500 font-mono">{c.phone}</div>
                <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-1">
                  {c.last_direction === "out" ? "↩ " : "→ "}
                  {c.last_message}
                </div>
                {c.unread > 0 && (
                  <span className="inline-block mt-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white">{c.unread} unread</span>
                )}
                {c.cases && c.cases.length > 0 && (
                  <span className="inline-block mt-1 ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{c.cases.length} case{c.cases.length > 1 ? "s" : ""}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className="flex-1 bg-slate-100">
          {selected ? <Thread phone={selected} onSent={load}/> : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400 flex-col gap-2">
              <span className="text-4xl">💬</span>
              Select a conversation
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
